import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Observable } from 'rxjs/Observable';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';
import { select, Store } from '@ngrx/store';
import { IState } from '../shared/models/state';
import { ContactEventsData, ContactsData, Deleter, FilterItem, Page } from './shared/models/Contact';
import {
  DELETE_CONTACT_TAG,
  DELETE_DATA,
  GET_DATA,
  GET_EVENTS,
  GET_FILTER_BY_TAGS,
  GET_FILTER_CALL_LENGTHS,
  GET_FILTER_EVENT_TYPES,
  GET_FILTER_MY_NUMBERS,
  GET_FILTER_USERS,
  GetColumnsSuccessAction,
  GetContactsFailureAction,
  GetContactsSuccessAction,
  GetDataFailureAction,
  GetEventsFailureAction,
  GetEventsSuccessAction,
  GetFilterItemsFailureAction,
  GetFilterItemsSuccessAction,
  GetRawColumnsSuccessAction,
  SET_TAGS_FOR_SELECTED
} from './contacts.actions';
import { ContactsService } from './shared/services/contacts.service';
import { TranslateService } from '@ngx-translate/core';
import { getEventsOptionSelector, getMassiveActionSelector, getTempPageSelector } from './contacts.reducer';
import { Msisdn } from '../shared/msisdn/msisdns';

@Injectable()
export class ContactsEffects {
  constructor(
    private actions$: Actions,
    public contactsService: ContactsService,
    public translate: TranslateService,
    private store: Store<IState>
  ) {}

  @Effect()
  getData: Observable<any> = this.actions$.pipe(
    ofType(GET_DATA),
    map((action: any) => action.payload),
    withLatestFrom(this.store.pipe(select(getEventsOptionSelector))),
    switchMap(([payload, eventsOption]: [{ page: Page; offset: number }, boolean]) => {
      if (payload.page.filter.users && !payload.page.filter.users.length) {
        delete payload.page.filter.users;
      }
      const potentialFilter = { ...payload.page.filter, contactId: null };

      const filtersModePagination = { ...payload.page.pagination, offset: payload.offset };
      const potentialPage = eventsOption
        ? { ...payload.page, filter: potentialFilter }
        : { ...payload.page, pagination: filtersModePagination };

      const columnDetails = {
        translation: null,
        name: 'DETAILS',
        type: 'NATIVE',
        key: 'NATIVE_DETAILS'
      };

      const columnAgent = {
        translation: null,
        name: 'AGENT',
        type: 'NATIVE',
        key: 'NATIVE_AGENT'
      };

      return this.contactsService.getOptionalData(eventsOption, potentialPage).pipe(
        switchMap((response: ContactsData) => {
          let filteredColumns: any;
          let integrationUser: string;
          const rawColumns = response.columns;

          response.columns = response.columns.find(item => item.key === 'NATIVE_DETAILS')
            ? response.columns
            : [...response.columns, columnDetails];

          response.columns = response.columns.find(item => item.key === 'NATIVE_AGENT')
            ? response.columns
            : [...response.columns, columnAgent];

          if (!eventsOption) {
            filteredColumns = response.columns.filter(
              col =>
                col.key !== 'NATIVE_EVENT_TYPE' &&
                col.key !== 'NATIVE_EVENT_STATUS_INFO' &&
                col.key !== 'NATIVE_NAME' &&
                col.key !== 'NATIVE_SURNAME' &&
                col.key !== 'NATIVE_EMAIL' &&
                col.key !== 'NATIVE_CREATOR_LOGIN' &&
                col.key !== 'NATIVE_AGENT_PHONE_NUMBER'
            );
          } else {
            filteredColumns = response.columns.filter(
              col =>
                col.key !== 'NATIVE_NAME' &&
                col.key !== 'NATIVE_EVENT_STATUS_INFO' &&
                col.key !== 'NATIVE_SURNAME' &&
                col.key !== 'NATIVE_EMAIL' &&
                col.key !== 'NATIVE_CREATOR_LOGIN' &&
                col.key !== 'NATIVE_AGENT_PHONE_NUMBER' &&
                col.key !== 'NATIVE_TAGS'
            );
          }

          const columnsOpaqued = this.contactsService.opaqueColumns(filteredColumns);
          const rawColumnsOpaqued = this.contactsService.opaqueColumns(rawColumns);

          let anonymousLabel;
          this.translate.get('UNKNOWN').subscribe(resp => {
            anonymousLabel = resp;
          });
          this.translate.get('CONTACTS_LIST.INTEGRATION_USER').subscribe(resp => {
            integrationUser = resp;
          });

          response.items.data.map(item => {
            const name = item['NATIVE_NAME'] ? item['NATIVE_NAME'] : anonymousLabel;
            const surname = item['NATIVE_SURNAME'] ? item['NATIVE_SURNAME'] : '';
            const email = item['NATIVE_EMAIL'] ? item['NATIVE_EMAIL'] : '';
            const agentLogin = item['NATIVE_CREATOR_LOGIN'] ? item['NATIVE_CREATOR_LOGIN'] : '';
            const agentNumber = item['NATIVE_AGENT_PHONE_NUMBER'] ? item['NATIVE_AGENT_PHONE_NUMBER'] : null;

            item.NATIVE_AGENT = {
              agentLogin: agentLogin === 'integration_user' ? integrationUser : agentLogin,
              agentNumber: agentNumber
            };

            item.NATIVE_DETAILS = {
              name: `${name} ${surname}`,
              email: email
            };
            return item;
          });

          return [
            new GetColumnsSuccessAction({ dataCol: columnsOpaqued, page: potentialPage }),
            new GetContactsSuccessAction({ data: response, page: potentialPage }),
            new GetRawColumnsSuccessAction(rawColumnsOpaqued)
          ];
        }),
        catchError(error => of(new GetContactsFailureAction(error)))
      );
    })
  );

  @Effect({ dispatch: false })
  deleteData: Observable<any> = this.actions$.pipe(
    ofType(DELETE_DATA),
    map((action: any) => action.payload),
    withLatestFrom(
      this.store.pipe(select(getEventsOptionSelector)),
      this.store.pipe(select(getTempPageSelector)),
      this.store.pipe(select(getMassiveActionSelector))
    ),
    switchMap(([payload, eventsOption, currentPage, massAction]: [Deleter, boolean, Page, boolean]) => {
      if (currentPage) {
        delete currentPage.filter.contactId;
      }
      if (massAction && payload.include && !payload.include.length) {
        delete payload.include;
      } else if (!massAction && payload.exclude && !payload.exclude.length) {
        delete payload.exclude;
      }

      if (!eventsOption) {
        const params = massAction ? { filter: currentPage.filter, ...payload } : payload;

        return this.contactsService.deleteContacts(params).pipe(
          map((response: any) => {
            this.contactsService.fetchDataAction(currentPage);
          }),
          catchError(error => of(new GetDataFailureAction(error)))
        );
      }
    })
  );

  @Effect({ dispatch: false })
  setTagsForSelected: Observable<any> = this.actions$.pipe(
    ofType(SET_TAGS_FOR_SELECTED),
    map((action: any) => action.payload),
    withLatestFrom(
      this.store.pipe(select(getEventsOptionSelector)),
      this.store.pipe(select(getTempPageSelector)),
      this.store.pipe(select(getMassiveActionSelector))
    ),
    switchMap(([payload, eventsOption, currentPage, massAction]: [Deleter, boolean, Page, boolean]) => {
      if (massAction && payload.include && !payload.include.length) {
        delete payload.include;
      } else if (!massAction && payload.exclude && !payload.exclude.length) {
        delete payload.exclude;
      }

      if (!eventsOption) {
        const params = massAction ? { filter: currentPage.filter, ...payload } : payload;

        return this.contactsService.setTagsForSelected(params).pipe(
          map((response: any) => {
            return this.contactsService.fetchDataAction(currentPage);
          }),
          catchError(error => of(new GetDataFailureAction(error)))
        );
      }
    })
  );

  @Effect({ dispatch: false })
  deleteContactTag: Observable<any> = this.actions$.pipe(
    ofType(DELETE_CONTACT_TAG),
    map((action: any) => action.payload),
    withLatestFrom(this.store.pipe(select(getEventsOptionSelector)), this.store.pipe(select(getTempPageSelector))),
    switchMap(([payload, eventsOption, currentPage]: [Deleter, boolean, Page]) => {
      if (!eventsOption) {
        return this.contactsService.removeTagsForSelected(payload).pipe(
          map((response: any) => {
            return this.contactsService.fetchDataAction(currentPage);
          }),
          catchError(error => of(new GetDataFailureAction(error)))
        );
      }
    })
  );

  @Effect()
  getEvents: Observable<any> = this.actions$.pipe(
    ofType(GET_EVENTS),
    map((action: any) => action.payload),
    switchMap((payload: any) => {
      const eventsPayload = payload;
      eventsPayload.pagination.offset = 0;
      return this.contactsService.getDetails(eventsPayload).pipe(
        map((response: ContactEventsData) => {
          if (payload.filter.contactId) {
            const expandedResponseItems = response.items.data.map(el => {
              return {
                ...el,
                recordId: eventsPayload.filter.contactId
              };
            });

            const newItems = {
              ...response.items,
              data: expandedResponseItems
            };

            const events = {
              ...response,
              items: newItems
            };

            return new GetEventsSuccessAction(events);
          }
        }),
        catchError(error => of(new GetEventsFailureAction(error)))
      );
    })
  );

  @Effect()
  getUsers: Observable<any> = this.actions$.pipe(
    ofType(GET_FILTER_USERS),
    map((action: any) => action.payload),
    withLatestFrom(this.store.pipe(select(getTempPageSelector))),
    switchMap(([payload, selectedTempItems]: [string, FilterItem[]]) => {
      return this.contactsService.getUsers().pipe(
        map((response: any[]) => {
          let usersList;

          if (
            selectedTempItems &&
            selectedTempItems.filter &&
            selectedTempItems.filter[payload] &&
            selectedTempItems.filter[payload].length
          ) {
            const selectedItemsNames = selectedTempItems.filter[payload];
            usersList = response.map(user => {
              if (selectedItemsNames.includes(user.username)) {
                return {
                  name: user.username,
                  type: 'users',
                  isFilter: true
                };
              } else {
                return {
                  name: user.username,
                  type: 'users',
                  isFilter: false
                };
              }
            });
          } else {
            usersList = response.map(user => ({ name: user.username, type: 'users', isFilter: false }));
          }

          return new GetFilterItemsSuccessAction({ list: usersList, filterName: 'users' });
        }),
        catchError(error => of(new GetFilterItemsFailureAction(error)))
      );
    })
  );

  @Effect()
  getMyPhoneNumbers: Observable<any> = this.actions$.pipe(
    ofType(GET_FILTER_MY_NUMBERS),
    map((action: any) => action.payload),
    withLatestFrom(this.store.pipe(select(getTempPageSelector))),
    switchMap(([payload, selectedTempItems]: [string, FilterItem[]]) => {
      return this.contactsService.getPhoneNumbers().pipe(
        map((response: Msisdn[]) => {
          let numbersList: FilterItem[];
          if (
            selectedTempItems &&
            selectedTempItems.filter &&
            selectedTempItems.filter[payload] &&
            selectedTempItems.filter[payload].length
          ) {
            const selectedItemsNames = selectedTempItems.filter[payload];
            numbersList = response.map(number => ({
              name: number.msisdn,
              type: 'callerIds',
              label: number.label,
              isFilter: selectedItemsNames.includes(number.msisdn),
              countryCode: number.isoCountry
            }));
          } else {
            numbersList = response.map(number => ({
              name: number.msisdn,
              label: number.label,
              type: 'callerIds',
              countryCode: number.isoCountry,
              isFilter: false
            }));
          }

          return new GetFilterItemsSuccessAction({ list: numbersList, filterName: 'callerIds' });
        }),
        catchError(error => of(new GetFilterItemsFailureAction(error)))
      );
    })
  );

  @Effect()
  getEventTypes: Observable<any> = this.actions$.pipe(
    ofType(GET_FILTER_EVENT_TYPES),
    map((action: any) => action.payload),
    withLatestFrom(this.store.pipe(select(getTempPageSelector))),
    switchMap(([payload, selectedTempItems]: [string, FilterItem[]]) => {
      return this.contactsService.getEventsTypes().pipe(
        map((response: string[]) => {
          let eventTypesList;
          if (
            selectedTempItems &&
            selectedTempItems.filter &&
            selectedTempItems.filter[payload] &&
            selectedTempItems.filter[payload].length
          ) {
            const selectedItemsNames = selectedTempItems.filter[payload];
            eventTypesList = response.map(eventType => {
              if (selectedItemsNames.includes(eventType)) {
                return {
                  name: eventType,
                  type: 'eventTypes',
                  isFilter: true
                };
              } else {
                return {
                  name: eventType,
                  type: 'eventTypes',
                  isFilter: false
                };
              }
            });
          } else {
            eventTypesList = response.map(eventType => ({ name: eventType, type: 'eventTypes', isFilter: false }));
          }

          const translatedFilteredItems = eventTypesList.map(item => {
            this.translate.get(`CONTACTS_LIST.STATUS.${item.name}`).subscribe(resp => {
              item = { ...item, translatedTitle: resp };
            });

            return item;
          });

          const sortedEventTypes = translatedFilteredItems.sort((a, b) => {
            return a.translatedTitle.localeCompare(b.translatedTitle, this.translate.currentLang);
          });

          return new GetFilterItemsSuccessAction({ list: sortedEventTypes, filterName: 'eventTypes' });
        }),
        catchError(error => of(new GetFilterItemsFailureAction(error)))
      );
    })
  );

  @Effect()
  getFilterByTags: Observable<any> = this.actions$.pipe(
    ofType(GET_FILTER_BY_TAGS),
    map((action: any) => action.payload),
    withLatestFrom(this.store.pipe(select(getTempPageSelector))),
    switchMap(([payload, selectedTempItems]: [string, FilterItem[]]) => {
      return this.contactsService.getFilterByTags().pipe(
        map((response: string[]) => {
          let usedTagsList;

          if (
            selectedTempItems &&
            selectedTempItems.filter &&
            selectedTempItems.filter[payload] &&
            selectedTempItems.filter[payload].length
          ) {
            const selectedItemsNames = selectedTempItems.filter[payload];
            usedTagsList = response.map(tag => {
              if (selectedItemsNames.includes(tag)) {
                return {
                  name: tag,
                  type: 'tags',
                  isFilter: true
                };
              } else {
                return {
                  name: tag,
                  type: 'tags',
                  isFilter: false
                };
              }
            });
          } else {
            usedTagsList = response.map(tag => ({ name: tag, type: 'tags', isFilter: false }));
          }

          const sortedTagsList = usedTagsList.sort((a, b) => {
            return a.name.localeCompare(b.name, this.translate.currentLang);
          });
          return new GetFilterItemsSuccessAction({ list: sortedTagsList, filterName: 'tags' });
        }),
        catchError(error => of(new GetFilterItemsFailureAction(error)))
      );
    })
  );

  @Effect()
  getCallLenghts: Observable<any> = this.actions$.pipe(
    ofType(GET_FILTER_CALL_LENGTHS),
    map((action: any) => action.payload),
    withLatestFrom(this.store.pipe(select(getTempPageSelector))),
    map(([payload, selectedTempPageItems]: [string, FilterItem[]]) => {
      let rangeItemsList;

      if (
        selectedTempPageItems &&
        selectedTempPageItems.filter &&
        selectedTempPageItems.filter[payload] &&
        selectedTempPageItems.filter[payload].length
      ) {
        const selectedItemsRanges = selectedTempPageItems.filter[payload];

        rangeItemsList = selectedItemsRanges.map((rangeItem, index) => {
          return {
            name: rangeItem.name,
            type: 'callLengths',
            isFilter: true,
            range: rangeItem
          };
        });
      } else {
        rangeItemsList = [
          {
            name: 'RANGE_1',
            range: {
              name: 'RANGE_1',
              minLengthSeconds: 0,
              maxLengthSeconds: 3599
            },
            type: 'callLengths',
            isFilter: false
          }
        ];
      }

      return new GetFilterItemsSuccessAction({ list: rangeItemsList, filterName: payload });
    }),
    catchError(error => of(new GetFilterItemsFailureAction(error)))
  );
}
